import { TMovementElementItem } from "../../../../models/salesModel"
import { getItemTaxes, getTotalTaxes, roundValue } from "../../../../utils"

export const resolveNoteItemId = (item: TMovementElementItem): number => {
  if (item.element_id) {
    return item.element_id
  }
  if (item.id > 0) {
    return item.id
  }
  return -1
}

export const getFinancialImpactMessage = (
  destination: string, 
  noteType: 'CREDIT_NOTE' | 'DEBIT_NOTE',
  usesCashSessions: boolean,
  hasActiveSession: boolean
): string => {
  if (destination === 'CREDIT') {
    if (noteType === 'CREDIT_NOTE') {
      return "Esta nota DISMINUIRÁ la cuenta por cobrar (cartera) del cliente."
    }
    return "Esta nota AUMENTARÁ la cuenta por cobrar (cartera) del cliente."
  }

  if (noteType === 'CREDIT_NOTE') {
    if (!usesCashSessions) {
      return "Esta nota registrará un egreso / reembolso de dinero en efectivo."
    }
    if (hasActiveSession) {
      return "Esta nota RESTARÁ del saldo de la caja activa (reembolso en efectivo)."
    }
    return "Esta nota registrará un reembolso en efectivo (sin vincular a una sesión de caja activa)."
  }

  if (!usesCashSessions) {
    return "Esta nota registrará un cobro / ingreso de dinero en efectivo."
  }
  if (hasActiveSession) {
    return "Esta nota SUMARÁ al saldo de la caja activa (cobro en efectivo recibido)."
  }
  return "Esta nota registrará un cobro en efectivo (sin vincular a una sesión de caja activa)."
}

export const computeItemPricing = (
  item: TMovementElementItem, 
  quantity: number, 
  taxesOptions: any[],
  customUnitPrice?: number
) => {
  const unitPrice = customUnitPrice ?? (item.unit_price || 0)
  const total_amount = roundValue(quantity * unitPrice)
  const discount_rate = item.discount_rate || 0
  const discount_amount = roundValue(total_amount * (discount_rate / 100))

  const itemWithOptions = {
    ...item,
    unit_price: unitPrice,
    quantity,
    total_amount,
    discount_amount,
    discount_rate,
    module_options: item.module_options || []
  }
  
  const taxes_list = getItemTaxes(itemWithOptions, taxesOptions)
  taxes_list.forEach(tax => {
    tax.tax_amount = roundValue(tax.tax_amount)
  })
  const total_tax_amount = roundValue(taxes_list.reduce((acc, el) => acc + el.tax_amount, 0))
  
  const total_amount_full = roundValue(total_amount + total_tax_amount)
  const total_amount_net = roundValue(total_amount - discount_amount + total_tax_amount)
  
  return {
    quantity,
    unit_price: unitPrice,
    total_amount,
    discount_amount,
    discount_rate,
    taxes_list,
    total_tax_amount,
    total_amount_full,
    total_amount_net
  }
}

export const calculateNoteTotals = (noteItems: TMovementElementItem[]) => {
  const total_amount = roundValue(noteItems.reduce((acc, item) => acc + (item.total_amount || 0), 0))
  const total_discount_amount = roundValue(noteItems.reduce((acc, item) => acc + (item.discount_amount || 0), 0))
  const taxes_list = getTotalTaxes(noteItems)
  taxes_list.forEach(tax => {
    tax.tax_amount = roundValue(tax.tax_amount)
  })
  const total_tax_amount = roundValue(taxes_list.reduce((acc, el) => acc + el.tax_amount, 0))
  const total_amount_full = roundValue(noteItems.reduce((acc, item) => acc + (item.total_amount_full || 0), 0))
  const total_amount_net = roundValue(noteItems.reduce((acc, item) => acc + (item.total_amount_net || 0), 0))
  
  return { 
    total_amount, 
    total_discount_amount, 
    total_tax_amount, 
    total_amount_full, 
    total_amount_net, 
    taxes_list 
  }
}
